import React, { useContext, useMemo, useState } from 'react';
import styles from './GroupedBar.module.scss';
import { EChartsOption } from 'echarts';
import { EChart } from '../../../EChart/EChart';
import { SFGrey, SFScrollable } from 'sfui';
import { AppearanceContext, MediaContext } from 'ui-smartforce-settings';
import {
  GroupedBarWidgetConfig,
  GroupedBarWidgetData,
  WidgetColor,
  WidgetFilterOption
} from '../../../../Models';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { WidgetFiltersPanel } from '../../WidgetFiltersPanel/WidgetFiltersPanel';
import { WidgetFilterDrawer } from '../../WidgetFilterDrawer/WidgetFilterDrawer';
import { formatName, tooltipFormatter } from '../../../../Helpers';
import { FilterSelector } from '../../FilterSelector/FilterSelector';

function getOptionsFromData(
  data: GroupedBarWidgetData[]
): WidgetFilterOption[] {
  return data.map((d) => ({ label: d.name, color: d.color as WidgetColor }));
}

function getChartOption(
  config: GroupedBarWidgetConfig,
  checked: string[],
  dayMode: boolean
): EChartsOption {
  return {
    tooltip: {
      className: 'echartsTooltip',
      confine: true,
      enterable: true,
      trigger: 'axis',
      position: (point) => {
        return [point[0], 0];
      },

      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: dayMode ? '#333' : '#CCC',
      borderWidth: 0,
      formatter: (params) => {
        return tooltipFormatter(
          params as CallbackDataParams[],
          config.data_label,
          config.data_label_singular,
          dayMode
        );
      }
    },
    xAxis: [
      {
        type: 'category',
        data: config.categories.map((c) => formatName(c)),
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          margin: 18,
          color: dayMode ? SFGrey[600] : SFGrey[400]
        },
        axisLine: {
          lineStyle: {
            color: dayMode ? SFGrey[200] : SFGrey[700]
          }
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          margin: 12,
          color: dayMode ? SFGrey[600] : SFGrey[400]
        },
        splitLine: {
          lineStyle: {
            color: dayMode ? '#E1E1E1' : '#333',
            type: 'dashed'
          }
        }
      }
    ],
    series: config.data
      .filter((d) => checked.includes(d.name))
      .map((d) => ({
        name: d.name,
        type: 'bar',
        barGap: 0,
        color: dayMode
          ? (d.color as WidgetColor).day
          : (d.color as WidgetColor).night,
        emphasis: {
          focus: 'series'
        },
        data: d.values
      }))
  };
}

export interface GroupedBarProps {
  title: string;
  data: GroupedBarWidgetConfig;
  isDrawerOpen?: boolean;
  onDrawerClose?: () => void;
}

export const GroupedBar = ({
  title,
  data,
  isDrawerOpen = false,
  ...props
}: GroupedBarProps): React.ReactElement<GroupedBarProps> => {
  const { isPhone } = useContext(MediaContext);
  const { themeType } = useContext(AppearanceContext);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [checked, setChecked] = useState<string[]>(
    getOptionsFromData(data.data).map((o) => o.label)
  );
  const options = useMemo(() => getOptionsFromData(data.data), [data]);

  const onDrawerClose = () => {
    props.onDrawerClose && props.onDrawerClose();
  };

  const onDrawerSave = (checked: string[]) => {
    setChecked(checked);
    props.onDrawerClose && props.onDrawerClose();
  };

  return (
    <SFScrollable className={styles.scrollable} containerClassName={styles.bar}>
      <EChart
        className={styles.echart}
        option={getChartOption(data, checked, themeType === 'day')}
      />

      <FilterSelector
        className={styles.filterSelectorForDownload}
        options={options}
        value={checked}
        onChange={(newChecked: string[]) => setChecked(newChecked)}
      />

      {!isPhone && (
        <WidgetFiltersPanel
          isOpen={isPanelOpen}
          options={options}
          checked={checked}
          onChange={(newChecked: string[]) => setChecked(newChecked)}
          onToggle={() => setIsPanelOpen((open: boolean) => !open)}
        />
      )}

      {isPhone && (
        <WidgetFilterDrawer
          title={title}
          defaultChecked={checked}
          options={options}
          isOpen={isDrawerOpen}
          onClose={onDrawerClose}
          onSave={onDrawerSave}
        />
      )}
    </SFScrollable>
  );
};
